import type { OrigemCarga } from './cargaSolta'
import type { AncoraOcupante, PerfilVeiculo } from './perfilVeiculo'
import type { ModoRetencao } from './retencaoOcupante'
import type { Vetor3 } from '../geometria/vetor3'
import { distanciaDaPolilinha } from './obstaculosExternos'

/**
 * Quem está no veículo.
 *
 * Substitui os escalares `idAncora`/`idPerfilVitima` de uma vítima única por
 * uma coleção: pessoas (que ocupam uma âncora do perfil, com ou sem cinto),
 * animais e objetos soltos (que não ocupam âncora — vários cabem no mesmo
 * lugar, como o banco traseiro real).
 */
export type TipoCorpo = 'pessoa' | 'animal' | 'objeto'
export type EstadoClinico = 'grave' | 'estavel'

/**
 * Triagem START — usada pelo Table top para decidir, na cena inteira, quem
 * sai primeiro quando há vários veículos e várias vítimas.
 *
 * `vermelho` = crítico (prioridade 1), `amarelo` = urgente (prioridade 2),
 * `verde` = leve/aguarda, `preto` = óbito ou sem prioridade de extração. É
 * mais granular que `EstadoClinico` (usado por Cinemática/Extração, que
 * lidam com um veículo só e não precisam ordenar entre vítimas de veículos
 * diferentes); as funções abaixo convertem entre os dois vocabulários sem
 * que essas abas precisem mudar de comportamento.
 */
export const CORES_TRIAGEM = ['vermelho', 'amarelo', 'verde', 'preto'] as const
export type PrioridadeTriagem = (typeof CORES_TRIAGEM)[number]

/** Ordem de gravidade da triagem: menor índice sai primeiro. */
export const ORDEM_TRIAGEM: Readonly<Record<PrioridadeTriagem, number>> = {
  vermelho: 0,
  amarelo: 1,
  verde: 2,
  preto: 3,
}

/** vermelho/preto → grave (retirada prioritária); amarelo/verde → estável. */
export function triagemParaEstadoClinico(triagem: PrioridadeTriagem): EstadoClinico {
  return triagem === 'vermelho' || triagem === 'preto' ? 'grave' : 'estavel'
}

/** Sem triagem prévia, o binário mapeia para o meio de cada faixa: grave → vermelho, estável → verde. */
export function estadoClinicoParaTriagem(estado: EstadoClinico): PrioridadeTriagem {
  return estado === 'grave' ? 'vermelho' : 'verde'
}

export interface Ocupante {
  id: string
  tipo: TipoCorpo
  nome: string
  /** âncora do perfil; para carga solta é só a origem do deslocamento, não reserva o lugar */
  idAncora: string
  /** `ProporcoesVitima['id']` para pessoa, `CorpoSolto['id']` para animal/objeto */
  idPerfil: string
  retencao: ModoRetencao
  /** classificação didática simples usada para ordenar a retirada */
  estadoClinico?: EstadoClinico
  /**
   * Compartimento de onde a carga solta parte. Ausente = o que o catálogo
   * declara em `origemPadrao`; a UI só grava aqui quando o aluno troca o lugar.
   * Não se aplica a pessoa, que parte da própria âncora.
   */
  compartimento?: OrigemCarga
}

/**
 * Uma segunda vítima é um obstáculo operacional quando o volume dos ombros
 * ocupa o corredor da trajetória. A margem representa o espaço de manejo ao
 * redor do corpo, sem impor qual vítima deve sair primeiro.
 */
export function vitimaInterfereNaTrajetoria(
  posicaoPelve: { x: number; z: number },
  larguraOmbros: number,
  pontosDaRota: readonly Vetor3[],
  margemOperacional = 0.32,
): boolean {
  return distanciaDaPolilinha(posicaoPelve, pontosDaRota) < larguraOmbros / 2 + margemOperacional
}

export const ID_OCUPANTE_PADRAO = 'ocupante-motorista'

/** Só o motorista, com cinto — o estado de hoje, para não regredir quem já usa a aba. */
export const OCUPANTES_PADRAO: readonly Ocupante[] = [
  {
    id: ID_OCUPANTE_PADRAO,
    tipo: 'pessoa',
    nome: 'Motorista',
    idAncora: 'motorista',
    idPerfil: 'adultoMasculino',
    retencao: 'comCinto',
    estadoClinico: 'grave',
  },
]

/** Uma pessoa por âncora; carga solta não disputa lugar com ninguém. */
export function podeAdicionar(lista: readonly Ocupante[], ocupante: Ocupante): boolean {
  if (lista.some((o) => o.id === ocupante.id)) return false
  if (ocupante.tipo !== 'pessoa') return true
  return !lista.some((o) => o.tipo === 'pessoa' && o.idAncora === ocupante.idAncora)
}

export function adicionar(lista: readonly Ocupante[], ocupante: Ocupante): Ocupante[] {
  return podeAdicionar(lista, ocupante) ? [...lista, ocupante] : [...lista]
}

export function remover(lista: readonly Ocupante[], id: string): Ocupante[] {
  return lista.filter((o) => o.id !== id)
}

export function comRetencao(lista: readonly Ocupante[], id: string, retencao: ModoRetencao): Ocupante[] {
  return lista.map((o) => (o.id === id ? { ...o, retencao } : o))
}

export function comEstadoClinico(lista: readonly Ocupante[], id: string, estadoClinico: EstadoClinico): Ocupante[] {
  return lista.map((o) => (o.id === id && o.tipo === 'pessoa' ? { ...o, estadoClinico } : o))
}

/** Grave primeiro; empates preservam a ordem de inclusão na cena. */
export function vitimasPorPrioridade(lista: readonly Ocupante[]): Ocupante[] {
  return pessoas(lista)
    .map((vitima, ordem) => ({ vitima, ordem }))
    .sort((a, b) => {
      const pa = a.vitima.estadoClinico === 'grave' ? 0 : 1
      const pb = b.vitima.estadoClinico === 'grave' ? 0 : 1
      return pa - pb || a.ordem - b.ordem
    })
    .map(({ vitima }) => vitima)
}

export function ocupantePorId(lista: readonly Ocupante[], id: string): Ocupante | null {
  return lista.find((o) => o.id === id) ?? null
}

export function pessoas(lista: readonly Ocupante[]): Ocupante[] {
  return lista.filter((o) => o.tipo === 'pessoa')
}

export function cargasSoltas(lista: readonly Ocupante[]): Ocupante[] {
  return lista.filter((o) => o.tipo !== 'pessoa')
}

/** Âncoras do perfil ainda sem pessoa — é o que a UI deve oferecer ao adicionar alguém. */
export function ancorasLivres(
  perfil: PerfilVeiculo,
  lista: readonly Ocupante[],
): AncoraOcupante[] {
  const ocupadas = new Set(pessoas(lista).map((o) => o.idAncora))
  return perfil.ancoras.filter((a) => !ocupadas.has(a.id))
}
